import { cva, type VariantProps } from 'class-variance-authority';
import { CircleCheck, Info, TriangleAlert } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Bandeau (`callout` des maquettes ; Alert shadcn/ui) : fond `card`, filet
 * `border`, rayon lg, texte 13px, icône 16px teintée selon la variante. Le
 * fond reste neutre : la teinte ne porte que sur l'icône (et le texte pour
 * `destructive`).
 */
const alertVariants = cva(
  [
    'relative flex w-full items-start gap-3 rounded-lg border border-border bg-card px-4 py-3 text-[13px] leading-[18px] text-card-foreground',
    '[&>svg]:mt-px [&>svg]:size-4 [&>svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: '[&>svg]:text-muted-foreground',
        info: '[&>svg]:text-info-fg',
        success: '[&>svg]:text-success-fg',
        warning: '[&>svg]:text-warning-fg',
        destructive: 'text-danger-fg [&>svg]:text-danger-fg',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type AlertVariant = NonNullable<VariantProps<typeof alertVariants>['variant']>;

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, role, ...props }, ref) => (
  <div
    ref={ref}
    role={role ?? (variant === 'destructive' ? 'alert' : 'status')}
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';

const AlertTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h5 ref={ref} className={cn('m-0 mb-0.5 font-semibold', className)} {...props} />
  ),
);
AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('min-w-0 flex-1 [&_p]:m-0', className)} {...props} />
));
AlertDescription.displayName = 'AlertDescription';

interface CalloutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: AlertVariant;
  /** Début en gras (« Les 33 échecs ont la même cause. »). */
  title?: React.ReactNode;
  /** Action à droite (bouton `outline` `size="sm"`). */
  action?: React.ReactNode;
  /** Icône à la place de celle de la variante. */
  icon?: React.ReactNode;
}

function defaultIcon(variant: AlertVariant): React.JSX.Element {
  switch (variant) {
    case 'success':
      return <CircleCheck aria-hidden="true" />;
    case 'info':
    case 'default':
      return <Info aria-hidden="true" />;
    case 'warning':
    case 'destructive':
      return <TriangleAlert aria-hidden="true" />;
  }
}

/** `callout` : icône + texte (titre en gras en tête) + action optionnelle. */
function Callout({
  variant = 'warning',
  title,
  action,
  icon,
  className,
  children,
  ...props
}: CalloutProps): React.JSX.Element {
  return (
    <Alert variant={variant} className={className} {...props}>
      {icon ?? defaultIcon(variant)}
      <div className="min-w-0 flex-1">
        {title !== undefined && title !== null ? <b className="font-semibold">{title} </b> : null}
        {children}
      </div>
      {action !== undefined && action !== null ? <div className="shrink-0">{action}</div> : null}
    </Alert>
  );
}

export { Alert, AlertTitle, AlertDescription, Callout };
